import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { config } from '../../utils/config';
import { storage } from '../../utils/storage';
import { Path } from '../../routes/components/path';
import {
  buildEpassiInitUrl,
  buildEpassiMobileDeepLink,
  isValidEpassiToken,
  setEpassiMobileNonce,
  setEpassiNonce,
} from '../../utils/epassi';
import { openEpassiWallet } from '../../nativePlugins/appEpassi';
import { pauseAppsFlyer, resumeAppsFlyerIfPaused } from '../../utils/analytics/appsflyerAnalytics';
import { useStoreEPassiWalletToken } from './useStoreEPassiWalletToken';

// Storage key for the page to return to once an Epassi activation redirect (web) or
// deep-link round trip (iOS) completes. Mirrors EDENRED_REDIRECT_KEY. Read by
// EPassiReturnHandler / EPassiMobileReturnHandler; falls back to Path.PaymentMethods
// (today's behavior) when unset, e.g. when activation was started from account settings.
export const EPASSI_REDIRECT_KEY = 'kotipizza.epassi_redirect';

export type EpassiActivationResult =
  | 'activated'
  | 'launched'
  | 'rejected'
  | 'failed'
  | 'not_installed';

type ActivateEpassiWallet = {
  // Android: resolves inline once the wallet token is fetched and stored — returnPath
  // is unused since the page never navigates away. iOS: opens the Epassi app and
  // resolves 'launched' immediately — the actual result arrives later via
  // EPassiMobileReturnHandler once the app returns; returnPath (checkout) is stored so
  // that handler brings the user back there, mirroring redirectToWebLogin.
  activateNative: (returnPath?: string) => Promise<EpassiActivationResult>;
  // Web only: full-page redirect to the Epassi WebUI. When returnPath is supplied it is
  // stored so EPassiReturnHandler brings the user back there once the round trip
  // completes (checkout); omitted, EPassiReturnHandler falls back to Path.PaymentMethods
  // (account settings).
  redirectToWebLogin: (returnPath?: string) => void;
  isLaunching: boolean;
  notInstalled: boolean;
};

// Shared by EPassiLoginButton (account settings) and the checkout payment gate. Never
// log or track the token value — only relay it to users-api via useStoreEPassiWalletToken.
export const useActivateEpassiWallet = (): ActivateEpassiWallet => {
  const { storeToken, reportRejected, reportFailed } = useStoreEPassiWalletToken();
  const [isLaunching, setIsLaunching] = useState(false);
  const [notInstalled, setNotInstalled] = useState(false);

  const activateNative = async (returnPath?: string): Promise<EpassiActivationResult> => {
    setNotInstalled(false);
    setIsLaunching(true);
    try {
      // Only the iOS return (an unsolicited appUrlOpen event) needs the anti-forgery
      // nonce and the AppsFlyer pause (its SDK inspects every URL that opens the app,
      // and the return URL carries the token); the Android token resolves inline from
      // our own activity result, invisible to AppsFlyer.
      if (Capacitor.getPlatform() === 'ios') {
        // Always set explicitly (never conditionally skip) — otherwise a stale key left
        // behind by an earlier, abandoned attempt from a different page (e.g. checkout)
        // would survive and hijack this attempt's return destination.
        if (returnPath) {
          storage.setItem(EPASSI_REDIRECT_KEY, returnPath);
        } else {
          storage.removeItem(EPASSI_REDIRECT_KEY);
        }
        setEpassiMobileNonce();
        await pauseAppsFlyer();
      }

      const result = await openEpassiWallet(
        buildEpassiMobileDeepLink(
          config.epassiPartnerName,
          config.epassiBenefitType,
          config.epassiApiKeyHash
        )
      );

      // Only a successful iOS launch leaves the app; on every other outcome the
      // round-trip is already over, so lift the AppsFlyer pause right away.
      if (result.kind !== 'launched') {
        await resumeAppsFlyerIfPaused();
      }

      switch (result.kind) {
        case 'launched':
          // iOS: EPassiMobileReturnHandler finishes the flow when the app returns.
          return 'launched';
        case 'token':
          if (isValidEpassiToken(result.token)) {
            const stored = await storeToken({ walletToken: result.token });
            return stored ? 'activated' : 'failed';
          }
          reportFailed();
          return 'failed';
        case 'cancelled':
          reportRejected();
          return 'rejected';
        case 'not_installed':
          setNotInstalled(true);
          return 'not_installed';
      }
    } catch {
      await resumeAppsFlyerIfPaused();
      reportFailed();
      return 'failed';
    } finally {
      setIsLaunching(false);
    }
  };

  const redirectToWebLogin = (returnPath?: string): void => {
    if (typeof window === 'undefined') return;

    // Always set explicitly (never conditionally skip) — otherwise a stale key left behind
    // by an earlier, abandoned attempt from a different page (e.g. checkout) would survive
    // and hijack this attempt's return destination.
    if (returnPath) {
      storage.setItem(EPASSI_REDIRECT_KEY, returnPath);
    } else {
      storage.removeItem(EPASSI_REDIRECT_KEY);
    }
    setEpassiNonce();
    // The derived return_url must match the URL registered with Epassi for our api key.
    const returnUrl = window.location.origin + Path.EPassiReturn;
    window.location.assign(
      buildEpassiInitUrl(config.epassiWebUiBaseUrl, config.epassiApiKeyHash, returnUrl)
    );
  };

  return { activateNative, redirectToWebLogin, isLaunching, notInstalled };
};
